export interface PatternPreset {
  id: string;
  name: string;
  imageData: ImageData;
}

export class PatternEditor {
  private container: HTMLElement;
  private previewCanvas!: HTMLCanvasElement;
  private tileCanvas!: HTMLCanvasElement;
  private presets: PatternPreset[] = [];
  private currentPattern: ImageData | null = null;
  private patternWidth: number = 32;
  private patternHeight: number = 32;
  private onChange: ((pattern: ImageData | null) => void) | null = null;

  constructor() {
    this.container = document.createElement("div");
    this.container.className = "pattern-editor";
    this._buildUI();
    this._initPresets();
  }

  private _initPresets(): void {
    // Create some default pattern presets
    this._createPreset("Checkerboard 8x8", this._generateCheckerboard(8, 8, { r: 200, g: 200, b: 200 }, { r: 100, g: 100, b: 100 }));
    this._createPreset("Checkerboard 16x16", this._generateCheckerboard(16, 16, { r: 200, g: 200, b: 200 }, { r: 100, g: 100, b: 100 }));
    this._createPreset("Dots", this._generateDots(32, 32, 8, { r: 180, g: 180, b: 180 }));
    this._createPreset("Stripes H", this._generateStripes(32, 32, "horizontal", { r: 200, g: 200, b: 200 }, { r: 120, g: 120, b: 120 }));
    this._createPreset("Stripes V", this._generateStripes(32, 32, "vertical", { r: 200, g: 200, b: 200 }, { r: 120, g: 120, b: 120 }));
    this._createPreset("Diagonal", this._generateDiagonal(32, 32, { r: 200, g: 200, b: 200 }, { r: 120, g: 120, b: 120 }));
    this._createPreset("Noise", this._generateNoise(32, 32));
    this._createPreset("Bricks", this._generateBricks(32, 32));
    this._renderPresetList();
  }

  private _createPreset(name: string, imageData: ImageData): void {
    this.presets.push({ id: crypto.randomUUID(), name, imageData });
  }

  private _generateCheckerboard(w: number, h: number, c1: { r: number; g: number; b: number }, c2: { r: number; g: number; b: number }): ImageData {
    const data = new ImageData(w, h);
    const cellW = w / 2, cellH = h / 2;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = (y * w + x) * 4;
        const c = (Math.floor(x / cellW) + Math.floor(y / cellH)) % 2 === 0 ? c1 : c2;
        data.data[idx] = c.r;
        data.data[idx + 1] = c.g;
        data.data[idx + 2] = c.b;
        data.data[idx + 3] = 255;
      }
    }
    return data;
  }

  private _generateDots(w: number, h: number, radius: number, color: { r: number; g: number; b: number }): ImageData {
    const data = new ImageData(w, h);
    const bg = { r: 240, g: 240, b: 240 };
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = (y * w + x) * 4;
        const cx = w / 2, cy = h / 2;
        const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
        const c = dist <= radius ? color : bg;
        data.data[idx] = c.r;
        data.data[idx + 1] = c.g;
        data.data[idx + 2] = c.b;
        data.data[idx + 3] = 255;
      }
    }
    return data;
  }

  private _generateStripes(w: number, h: number, direction: "horizontal" | "vertical", c1: { r: number; g: number; b: number }, c2: { r: number; g: number; b: number }): ImageData {
    const data = new ImageData(w, h);
    const stripeW = w / 4;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = (y * w + x) * 4;
        const val = direction === "horizontal" ? y : x;
        const c = Math.floor(val / stripeW) % 2 === 0 ? c1 : c2;
        data.data[idx] = c.r;
        data.data[idx + 1] = c.g;
        data.data[idx + 2] = c.b;
        data.data[idx + 3] = 255;
      }
    }
    return data;
  }

  private _generateDiagonal(w: number, h: number, c1: { r: number; g: number; b: number }, c2: { r: number; g: number; b: number }): ImageData {
    const data = new ImageData(w, h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = (y * w + x) * 4;
        const c = (x + y) % 8 < 4 ? c1 : c2;
        data.data[idx] = c.r;
        data.data[idx + 1] = c.g;
        data.data[idx + 2] = c.b;
        data.data[idx + 3] = 255;
      }
    }
    return data;
  }

  private _generateNoise(w: number, h: number): ImageData {
    const data = new ImageData(w, h);
    for (let i = 0; i < w * h * 4; i += 4) {
      const v = Math.random() * 255;
      data.data[i] = v;
      data.data[i + 1] = v;
      data.data[i + 2] = v;
      data.data[i + 3] = 255;
    }
    return data;
  }

  private _generateBricks(w: number, h: number): ImageData {
    const data = new ImageData(w, h);
    const brickColor = { r: 180, g: 80, b: 60 };
    const mortarColor = { r: 200, g: 200, b: 200 };
    const brickH = h / 4;
    const brickW = w / 2;
    const mortar = 2;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = (y * w + x) * 4;
        const row = Math.floor(y / brickH);
        const offsetX = row % 2 === 0 ? 0 : brickW / 2;
        const bx = (x + offsetX) % w;
        const inMortarY = y % brickH < mortar;
        const inMortarX = bx % brickW < mortar;
        const c = inMortarY || inMortarX ? mortarColor : brickColor;
        data.data[idx] = c.r;
        data.data[idx + 1] = c.g;
        data.data[idx + 2] = c.b;
        data.data[idx + 3] = 255;
      }
    }
    return data;
  }

  private _buildUI(): void {
    this.container.innerHTML = `
      <div class="panel-title">Pattern Editor</div>
      <div class="pattern-editor__content">
        <div class="pattern-preview">
          <canvas id="pattern-preview-canvas" width="128" height="128"></canvas>
        </div>
        <div class="pattern-tile-preview">
          <label class="muted">Tile Preview:</label>
          <canvas id="pattern-tile-canvas" width="96" height="96"></canvas>
        </div>
        <div class="pattern-controls">
          <div class="brush-row">
            <label>Width</label>
            <input type="number" min="1" max="256" value="32" id="pattern-width" style="width:50px">
          </div>
          <div class="brush-row">
            <label>Height</label>
            <input type="number" min="1" max="256" value="32" id="pattern-height" style="width:50px">
          </div>
        </div>
        <div class="pattern-presets">
          <label class="muted">Presets:</label>
          <div class="pattern-preset-list" id="pattern-preset-list"></div>
        </div>
        <div class="pattern-actions">
          <button class="btn-sm" id="pattern-from-selection">From Selection</button>
          <button class="btn-sm" id="pattern-save-set">Save Set</button>
          <button class="btn-sm" id="pattern-load-set">Load Set</button>
        </div>
      </div>
    `;

    this.previewCanvas = this.container.querySelector("#pattern-preview-canvas")!;
    this.tileCanvas = this.container.querySelector("#pattern-tile-canvas")!;
    this._bindEvents();
  }

  private _bindEvents(): void {
    const widthInput = this.container.querySelector("#pattern-width") as HTMLInputElement;
    const heightInput = this.container.querySelector("#pattern-height") as HTMLInputElement;

    widthInput?.addEventListener("change", () => {
      this.patternWidth = Math.max(1, Math.min(256, parseInt(widthInput.value) || 32));
      this._updatePreviews();
    });

    heightInput?.addEventListener("change", () => {
      this.patternHeight = Math.max(1, Math.min(256, parseInt(heightInput.value) || 32));
      this._updatePreviews();
    });

    this.container.querySelector("#pattern-from-selection")?.addEventListener("click", () => {
      this._createFromSelection();
    });

    this.container.querySelector("#pattern-save-set")?.addEventListener("click", () => {
      this._saveSet();
    });

    this.container.querySelector("#pattern-load-set")?.addEventListener("click", () => {
      this._loadSet();
    });
  }

  private _renderPresetList(): void {
    const list = this.container.querySelector("#pattern-preset-list") as HTMLElement;
    if (!list) return;
    list.innerHTML = "";
    list.style.cssText = "max-height:120px;overflow-y:auto;display:grid;grid-template-columns:repeat(4,1fr);gap:4px;margin-top:4px;";
    for (const preset of this.presets) {
      const item = document.createElement("div");
      item.style.cssText = "width:24px;height:24px;border:1px solid #444;cursor:pointer;";
      item.title = preset.name;
      const thumb = document.createElement("canvas");
      thumb.width = preset.imageData.width;
      thumb.height = preset.imageData.height;
      thumb.style.width = "100%";
      thumb.style.height = "100%";
      thumb.style.imageRendering = "pixelated";
      thumb.getContext("2d")!.putImageData(preset.imageData, 0, 0);
      item.appendChild(thumb);
      item.addEventListener("click", () => {
        this.currentPattern = preset.imageData;
        this.patternWidth = preset.imageData.width;
        this.patternHeight = preset.imageData.height;
        (this.container.querySelector("#pattern-width") as HTMLInputElement).value = String(this.patternWidth);
        (this.container.querySelector("#pattern-height") as HTMLInputElement).value = String(this.patternHeight);
        this._updatePreviews();
        this._emitChange();
      });
      list.appendChild(item);
    }
  }

  private _updatePreviews(): void {
    if (!this.currentPattern) return;

    // Single tile preview
    const tileCtx = this.tileCanvas.getContext("2d")!;
    tileCtx.clearRect(0, 0, this.tileCanvas.width, this.tileCanvas.height);
    tileCtx.imageSmoothingEnabled = false;
    tileCtx.putImageData(this.currentPattern, 0, 0);

    // Tiled preview
    const ctx = this.previewCanvas.getContext("2d")!;
    ctx.clearRect(0, 0, this.previewCanvas.width, this.previewCanvas.height);
    ctx.imageSmoothingEnabled = false;
    const pw = this.currentPattern.width;
    const ph = this.currentPattern.height;
    for (let y = 0; y < this.previewCanvas.height; y += ph) {
      for (let x = 0; x < this.previewCanvas.width; x += pw) {
        ctx.putImageData(this.currentPattern, x, y);
      }
    }
  }

  private _createFromSelection(): void {
    // This would be called from the main app with actual selection data
    // For now, create a simple pattern
    const w = this.patternWidth;
    const h = this.patternHeight;
    const data = new ImageData(w, h);
    for (let i = 0; i < w * h * 4; i += 4) {
      data.data[i] = Math.random() * 255;
      data.data[i + 1] = Math.random() * 255;
      data.data[i + 2] = Math.random() * 255;
      data.data[i + 3] = 255;
    }
    this.currentPattern = data;
    this._updatePreviews();
    this._emitChange();
  }

  private _saveSet(): void {
    const data = this.presets.map(p => ({
      name: p.name,
      width: p.imageData.width,
      height: p.imageData.height,
      pixels: Array.from(p.imageData.data),
    }));
    const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "patterns.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  private _loadSet(): void {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = JSON.parse(reader.result as string);
          if (Array.isArray(data)) {
            for (const item of data) {
              const imageData = new ImageData(item.width, item.height);
              imageData.data.set(new Uint8ClampedArray(item.pixels));
              this.presets.push({ id: crypto.randomUUID(), name: item.name, imageData });
            }
            this._renderPresetList();
          }
        } catch {
          alert("Failed to load pattern set.");
        }
      };
      reader.readAsText(file);
    });
    input.click();
  }

  private _emitChange(): void {
    if (this.onChange) {
      this.onChange(this.currentPattern);
    }
  }

  setCallback(onChange: (pattern: ImageData | null) => void): void {
    this.onChange = onChange;
  }

  getPattern(): ImageData | null {
    return this.currentPattern;
  }

  setPattern(imageData: ImageData): void {
    this.currentPattern = imageData;
    this.patternWidth = imageData.width;
    this.patternHeight = imageData.height;
    (this.container.querySelector("#pattern-width") as HTMLInputElement).value = String(this.patternWidth);
    (this.container.querySelector("#pattern-height") as HTMLInputElement).value = String(this.patternHeight);
    this._updatePreviews();
    this._emitChange();
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }

  destroy(): void {
    this.container.remove();
  }
}
